import { PartnerSettingValue } from '@provision/entity-types';
import { useCallback, useMemo, useState } from 'react';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  PartnerSetting,
  useManagePartnerSettingsValue,
  usePartnerSettings,
} from 'src/graphql/entities/partner-setting';
import { WorkflowTriggerType } from 'src/graphql/entities/workflow/workflow.trigger';
import {
  EMPTY_WORKFLOW_EMERGENCY_OVERRIDE_CONFIG,
  TriggerEmergencyOverride,
  WorkflowEmergencyOverrideConfig,
} from 'src/pages/Settings/preferences/hooks/workflow-emergency-override.constants';

export interface UseWorkflowEmergencyOverrideResult {
  config: WorkflowEmergencyOverrideConfig;
  loading: boolean;
  isSaving: boolean;
  /** Turns every trigger's override on or off in one write. */
  setMasterEnabled: (enabled: boolean) => Promise<void>;
  setTriggerEnabled: (triggerType: WorkflowTriggerType, enabled: boolean) => Promise<void>;
  setTriggerWorkflow: (triggerType: WorkflowTriggerType, workflowId: string) => Promise<void>;
  /** Drops a trigger's workflow *and* its enabled flag. See the implementation for why. */
  clearTriggerWorkflow: (triggerType: WorkflowTriggerType) => Promise<void>;
}

/**
 * Reads and writes the emergency override config on the platform partner's setting row.
 *
 * Every mutator persists immediately rather than staging a draft behind a Save button:
 * this is a break-glass control reached mid-incident, so a flip has to be one click. Each
 * write carries the whole config, so the mutators merge onto what is already stored
 * instead of replacing it — flipping the master switch must not wipe the per-trigger
 * selections an operator set up in advance.
 */
export function useWorkflowEmergencyOverride(): UseWorkflowEmergencyOverrideResult {
  const [isSaving, setIsSaving] = useState(false);

  const { data, loading } = usePartnerSettings({
    partnerId: PLATFORM_PARTNER_ID,
    partnerSetting: PartnerSetting.WorkflowEmergencyOverride,
  });
  const { createPartnerSettingsValue, updatePartnerSettingsValue } =
    useManagePartnerSettingsValue(PLATFORM_PARTNER_ID);

  const platformRecord = useMemo(
    () =>
      data.find(
        (record) =>
          record.partnerId === PLATFORM_PARTNER_ID &&
          record.partnerSettingId === PartnerSetting.WorkflowEmergencyOverride,
      ) ?? null,
    [data],
  );

  const config = useMemo(() => parseConfig(platformRecord?.value), [platformRecord?.value]);

  const persist = useCallback(
    async (nextConfig: WorkflowEmergencyOverrideConfig) => {
      setIsSaving(true);
      const value = JSON.stringify(nextConfig);

      try {
        if (platformRecord?.partnerSettingValueId) {
          await updatePartnerSettingsValue({
            partnerSettingValueId: platformRecord.partnerSettingValueId,
            partnerSettingId: PartnerSetting.WorkflowEmergencyOverride,
            value,
          });
        } else {
          await createPartnerSettingsValue({
            partnerSettingId: PartnerSetting.WorkflowEmergencyOverride,
            value,
          } as Partial<PartnerSettingValue>);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [platformRecord, createPartnerSettingsValue, updatePartnerSettingsValue],
  );

  const setMasterEnabled = useCallback(
    (enabled: boolean) => persist({ ...config, master: enabled }),
    [config, persist],
  );

  const setTriggerEnabled = useCallback(
    (triggerType: WorkflowTriggerType, enabled: boolean) =>
      persist(withTrigger(config, triggerType, { enabled })),
    [config, persist],
  );

  const setTriggerWorkflow = useCallback(
    (triggerType: WorkflowTriggerType, workflowId: string) =>
      persist(withTrigger(config, triggerType, { workflowId })),
    [config, persist],
  );

  /**
   * Forgets a trigger's workflow, and its enabled flag with it.
   *
   * Clearing the workflow alone would leave `enabled: true` lying in the config: harmless
   * while nothing is selected, but the moment someone picked a workflow — to prepare, not to
   * fire — the trigger would go live on the spot. An override should only ever start running
   * because somebody threw the switch.
   */
  const clearTriggerWorkflow = useCallback(
    (triggerType: WorkflowTriggerType) =>
      persist(withTrigger(config, triggerType, { workflowId: undefined, enabled: false })),
    [config, persist],
  );

  return {
    config,
    loading,
    isSaving,
    setMasterEnabled,
    setTriggerEnabled,
    setTriggerWorkflow,
    clearTriggerWorkflow,
  };
}

function parseConfig(rawValue: string | null | undefined): WorkflowEmergencyOverrideConfig {
  if (!rawValue) return EMPTY_WORKFLOW_EMERGENCY_OVERRIDE_CONFIG;

  try {
    const parsed: unknown = JSON.parse(rawValue);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as WorkflowEmergencyOverrideConfig)
      : EMPTY_WORKFLOW_EMERGENCY_OVERRIDE_CONFIG;
  } catch {
    return EMPTY_WORKFLOW_EMERGENCY_OVERRIDE_CONFIG;
  }
}

function withTrigger(
  config: WorkflowEmergencyOverrideConfig,
  triggerType: WorkflowTriggerType,
  patch: TriggerEmergencyOverride,
): WorkflowEmergencyOverrideConfig {
  return {
    ...config,
    triggers: {
      ...config.triggers,
      [triggerType]: { ...config.triggers?.[triggerType], ...patch },
    },
  };
}
